import React, { useCallback, useEffect, useRef, useState } from "react";
import { PageHeader } from "../components/PageHeader";
import api, { formatApiError, formatINR, API } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import CustomFieldsSection from "../components/CustomFieldsSection";
import {
  Camera, User, Envelope, Phone, House, LockKey, Upload, Download,
  Trash, FilePdf, FileDoc, ImageSquare, CheckCircle, Buildings, Calendar, IdentificationCard, Money, ShieldCheck,
} from "@phosphor-icons/react";

const DOC_SLOTS = [
  { key: "aadhaar", label: "Aadhaar" },
  { key: "pan", label: "PAN" },
  { key: "resume", label: "Resume" },
  { key: "address_proof", label: "Address Proof" },
  { key: "other", label: "Other" },
];

const ACCEPT_DOC = ".pdf,.doc,.docx,.jpg,.jpeg,.png";
const ACCEPT_PHOTO = ".jpg,.jpeg,.png";

export default function Profile() {
  const { user, refresh } = useAuth();
  const [me, setMe] = useState(null);
  const [contact, setContact] = useState({ phone: "", email: "", address: "" });
  const [customFields, setCustomFields] = useState({});
  const [saving, setSaving] = useState(false);
  const [savingCF, setSavingCF] = useState(false);
  const [photoVersion, setPhotoVersion] = useState(0); // busts avatar cache after upload

  const load = useCallback(async () => {
    if (!user?.id) return;
    const { data } = await api.get(`/employees/${user.id}`);
    setMe(data);
    setContact({
      phone: data.phone || "",
      email: data.email || "",
      address: data.address || "",
    });
    setCustomFields(data.custom_fields || {});
  }, [user?.id]);

  useEffect(() => { load(); }, [load]);

  const saveContact = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`/employees/me/self`, contact);
      toast.success("Profile updated");
      await load();
      await refresh(); // update header / sidebar with new email if changed
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const saveCustomFields = async () => {
    setSavingCF(true);
    try {
      await api.put(`/employees/me/self`, { custom_fields: customFields });
      toast.success("Custom fields saved");
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSavingCF(false); }
  };

  const photoUrl = me?.photo_path ? `${API}/employees/${user.id}/photo?v=${photoVersion}` : null;

  const onPhotoPicked = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/employees/me/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Profile photo updated");
      setPhotoVersion(Date.now());
      await load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const removePhoto = async () => {
    if (!window.confirm("Remove your profile photo?")) return;
    try {
      await api.delete(`/employees/me/photo`);
      toast.success("Photo removed");
      setPhotoVersion(Date.now());
      await load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  if (!me) {
    return (
      <div className="p-6 text-sm text-[#6B7280]" data-testid="profile-loading">Loading profile…</div>
    );
  }

  const initials = (me.name || "").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase() || "?";

  return (
    <div data-testid="profile-page">
      <PageHeader
        eyebrow="My Profile"
        title="Your profile"
        subtitle="Keep your contact info current, change your password and upload your documents."
      />
      <div className="p-4 sm:p-6 lg:p-8 space-y-6">
        {/* Avatar + identity */}
        <div className="bg-white border border-[#E5E7EB] p-6 flex flex-col sm:flex-row gap-6 items-start">
          <div className="relative">
            <div className="w-28 h-28 rounded-full overflow-hidden bg-[#106109]/10 border-2 border-[#106109]/20 flex items-center justify-center">
              {photoUrl ? (
                <img src={photoUrl} alt={me.name} className="w-full h-full object-cover" data-testid="profile-avatar-img" />
              ) : (
                <span className="text-3xl font-display font-black text-[#106109]" data-testid="profile-avatar-initials">{initials}</span>
              )}
            </div>
            <label
              className="absolute -bottom-1 -right-1 bg-[#106109] text-white rounded-full p-2 cursor-pointer hover:bg-[#0d4f07] shadow-md"
              title="Upload photo"
              data-testid="profile-photo-upload"
            >
              <Camera size={16} weight="fill" />
              <input type="file" accept={ACCEPT_PHOTO} className="hidden"
                     onChange={(e) => e.target.files[0] && onPhotoPicked(e.target.files[0])} />
            </label>
          </div>
          <div className="flex-1 space-y-1">
            <div className="label-caps">{me.role}</div>
            <div className="font-display font-black text-3xl tracking-tight">{me.name}</div>
            <div className="text-sm text-[#6B7280] font-mono">{me.employee_id}</div>
            <div className="text-sm text-[#374151] mt-2">
              {me.job_role} · {me.department}
            </div>
            {me.date_of_joining && (
              <div className="text-xs text-[#6B7280]">Joined {me.date_of_joining}</div>
            )}
          </div>
          {me.photo_path && (
            <button onClick={removePhoto} data-testid="profile-photo-remove"
                    className="text-xs text-[#DC2626] hover:underline inline-flex items-center gap-1">
              <Trash size={12} /> Remove photo
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Contact info */}
          <form onSubmit={saveContact} className="bg-white border border-[#E5E7EB] p-6 space-y-4" data-testid="contact-form">
            <div>
              <div className="label-caps">Contact info</div>
              <h3 className="font-display font-bold text-xl mt-1">Editable details</h3>
              <p className="text-xs text-[#6B7280] mt-1">
                Name, department, role and salary can only be updated by HR/Admin.
              </p>
            </div>
            <ProfileField label="Phone" icon={Phone}>
              <input value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })}
                     data-testid="contact-phone" placeholder="+91 98..."
                     className="w-full border border-[#E5E7EB] pl-10 pr-3 py-2.5 text-sm focus:outline-none focus:border-[#106109]" />
            </ProfileField>
            <ProfileField label="Email" icon={Envelope}>
              <input type="email" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })}
                     data-testid="contact-email"
                     className="w-full border border-[#E5E7EB] pl-10 pr-3 py-2.5 text-sm focus:outline-none focus:border-[#106109]" />
            </ProfileField>
            <ProfileField label="Current address" icon={House} textarea>
              <textarea rows={3} value={contact.address}
                        onChange={(e) => setContact({ ...contact, address: e.target.value })}
                        data-testid="contact-address"
                        placeholder="Flat / Street / City / State / PIN"
                        className="w-full border border-[#E5E7EB] pl-10 pr-3 py-2.5 text-sm focus:outline-none focus:border-[#106109]" />
            </ProfileField>
            <button type="submit" disabled={saving} data-testid="contact-save"
                    className="brand-btn w-full py-2.5 text-sm font-semibold disabled:opacity-60">
              {saving ? "Saving…" : "Save contact info"}
            </button>
          </form>

          {/* Password */}
          <ChangePasswordCard />
        </div>

        {/* Employment info (read-only) */}
        <EmploymentInfoCard me={me} />

        {/* Custom Fields — editable by both employee and admin */}
        <CustomFieldsCard
          values={customFields}
          onChange={setCustomFields}
          onSave={saveCustomFields}
          saving={savingCF}
        />

        {/* Documents */}
        <div className="bg-white border border-[#E5E7EB]">
          <div className="px-6 py-4 border-b border-[#E5E7EB]">
            <div className="label-caps">KYC & Documents</div>
            <h3 className="font-display font-bold text-xl mt-1">Your documents</h3>
            <p className="text-xs text-[#6B7280] mt-1">
              JPEG, PNG, PDF, DOC or DOCX up to 10 MB each. Visible only to you and HR/Admin.
            </p>
          </div>
          <ul className="divide-y divide-[#F3F4F6]">
            {DOC_SLOTS.map((slot) => (
              <DocumentRow
                key={slot.key}
                slot={slot}
                empId={user.id}
                entry={me.documents?.[slot.key] || null}
                onChanged={load}
              />
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

function ProfileField({ label, icon: Icon, children, textarea }) {
  return (
    <div>
      <label className="label-caps block mb-1.5">{label}</label>
      <div className="relative">
        <Icon size={16} className={`absolute left-3 ${textarea ? "top-3" : "top-1/2 -translate-y-1/2"} text-[#6B7280]`} />
        {children}
      </div>
    </div>
  );
}

function ChangePasswordCard() {
  const [current, setCurrent] = useState("");
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pwd.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (pwd !== pwd2) { toast.error("Passwords do not match"); return; }
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: pwd });
      toast.success("Password updated");
      setCurrent(""); setPwd(""); setPwd2("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className="bg-white border border-[#E5E7EB] p-6 space-y-4" data-testid="password-form">
      <div>
        <div className="label-caps">Security</div>
        <h3 className="font-display font-bold text-xl mt-1">Change password</h3>
        <p className="text-xs text-[#6B7280] mt-1">
          Use at least 6 characters. You'll stay signed in on this device.
        </p>
      </div>
      <ProfileField label="Current password" icon={LockKey}>
        <input type="password" required value={current} onChange={(e) => setCurrent(e.target.value)}
               data-testid="password-current" autoComplete="current-password"
               className="w-full border border-[#E5E7EB] pl-10 pr-3 py-2.5 text-sm focus:outline-none focus:border-[#106109]" />
      </ProfileField>
      <ProfileField label="New password" icon={LockKey}>
        <input type="password" required minLength={6} value={pwd} onChange={(e) => setPwd(e.target.value)}
               data-testid="password-new" autoComplete="new-password"
               className="w-full border border-[#E5E7EB] pl-10 pr-3 py-2.5 text-sm focus:outline-none focus:border-[#106109]" />
      </ProfileField>
      <ProfileField label="Confirm new password" icon={LockKey}>
        <input type="password" required minLength={6} value={pwd2} onChange={(e) => setPwd2(e.target.value)}
               data-testid="password-confirm" autoComplete="new-password"
               className="w-full border border-[#E5E7EB] pl-10 pr-3 py-2.5 text-sm focus:outline-none focus:border-[#106109]" />
      </ProfileField>
      <button type="submit" disabled={saving} data-testid="password-save"
              className="brand-btn w-full py-2.5 text-sm font-semibold disabled:opacity-60">
        {saving ? "Updating…" : "Update password"}
      </button>
    </form>
  );
}

function DocumentRow({ slot, empId, entry, onChanged }) {
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/employees/me/documents/${slot.key}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${slot.label} uploaded`);
      onChanged();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); if (inputRef.current) inputRef.current.value = ""; }
  };

  const download = async () => {
    try {
      const res = await api.get(`/employees/${empId}/documents/${slot.key}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = entry?.filename || slot.key; a.click();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error("Download failed"); }
  };

  const remove = async () => {
    if (!window.confirm(`Remove ${slot.label}?`)) return;
    try {
      await api.delete(`/employees/me/documents/${slot.key}`);
      toast.success(`${slot.label} removed`);
      onChanged();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <li className="px-6 py-4 flex items-center gap-4 flex-wrap" data-testid={`doc-row-${slot.key}`}>
      <div className="w-10 h-10 flex items-center justify-center border border-[#E5E7EB] text-[#106109]">
        <DocIcon entry={entry} />
      </div>
      <div className="flex-1 min-w-[180px]">
        <div className="font-semibold text-sm text-[#111827]">{slot.label}</div>
        {entry ? (
          <div className="text-xs text-[#6B7280] mt-0.5">
            {entry.filename} · {Math.max(1, Math.round((entry.size || 0) / 1024))} KB
          </div>
        ) : (
          <div className="text-xs text-[#9CA3AF] italic mt-0.5">Not uploaded</div>
        )}
      </div>
      {entry && <CheckCircle size={16} weight="fill" className="text-[#10B981]" />}
      <div className="flex items-center gap-2">
        {entry && (
          <button onClick={download} data-testid={`doc-download-${slot.key}`}
                  className="border border-[#E5E7EB] p-2 hover:bg-[#F9FAFB]" title="Download">
            <Download size={14} />
          </button>
        )}
        <label className="border border-[#E5E7EB] p-2 hover:bg-[#F9FAFB] cursor-pointer inline-flex items-center gap-1 text-xs"
               title={entry ? "Replace" : "Upload"} data-testid={`doc-upload-${slot.key}`}>
          <Upload size={14} />
          <span className="hidden sm:inline">{entry ? "Replace" : "Upload"}</span>
          <input ref={inputRef} type="file" accept={ACCEPT_DOC} className="hidden"
                 disabled={busy}
                 onChange={(e) => upload(e.target.files[0])} />
        </label>
        {entry && (
          <button onClick={remove} data-testid={`doc-remove-${slot.key}`}
                  className="border border-[#E5E7EB] p-2 hover:bg-[#FEF2F2] text-[#DC2626]" title="Remove">
            <Trash size={14} />
          </button>
        )}
      </div>
    </li>
  );
}

function DocIcon({ entry }) {
  if (!entry) return <User size={18} />;
  const ct = (entry.content_type || "").toLowerCase();
  if (ct.startsWith("image/")) return <ImageSquare size={18} />;
  if (ct.includes("pdf")) return <FilePdf size={18} />;
  return <FileDoc size={18} />;
}

function EmploymentInfoCard({ me }) {
  const rows = [
    { icon: IdentificationCard, label: "Employee ID", value: me.employee_id },
    { icon: User, label: "Full name", value: me.name },
    { icon: Buildings, label: "Department", value: me.department },
    { icon: ShieldCheck, label: "Job role", value: me.job_role },
    { icon: Calendar, label: "Date of joining", value: me.date_of_joining || "—" },
    { icon: User, label: "Access level", value: me.role },
    { icon: Money, label: "Basic salary (₹ / month)", value: formatINR(me.basic_salary || 0) },
    { icon: ShieldCheck, label: "Security question", value: me.security_question || "—" },
  ];
  return (
    <div className="bg-white border border-[#E5E7EB] p-6" data-testid="employment-info-card">
      <div className="label-caps">Employment info</div>
      <h3 className="font-display font-bold text-xl mt-1">HR-managed details</h3>
      <p className="text-xs text-[#6B7280] mt-1">
        These fields are maintained by HR/Admin. Reach out to your admin if anything looks off.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-4">
        {rows.map((r) => (
          <div key={r.label} className="border border-[#F3F4F6] px-3 py-2.5">
            <div className="flex items-center gap-1.5 text-[#106109]">
              <r.icon size={14} weight="bold" />
              <span className="label-caps text-[10px]">{r.label}</span>
            </div>
            <div className="text-sm font-semibold text-[#111827] mt-1 truncate" title={r.value}>{r.value}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function CustomFieldsCard({ values, onChange, onSave, saving }) {
  const [hasFields, setHasFields] = useState(null);
  useEffect(() => {
    api.get("/custom-fields", { params: { entity: "employee" } })
      .then(({ data }) => setHasFields(data.length > 0))
      .catch(() => setHasFields(false));
  }, []);
  if (hasFields === false) return null; // hide card entirely when admin hasn't defined any fields
  return (
    <div className="bg-white border border-[#E5E7EB] p-6" data-testid="custom-fields-card">
      <div className="label-caps">Additional details</div>
      <h3 className="font-display font-bold text-xl mt-1">Custom fields</h3>
      <p className="text-xs text-[#6B7280] mt-1">
        These fields were configured by your admin under Settings → Custom Fields → Employee. You can update them here.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
        <CustomFieldsSection entity="employee" values={values} onChange={onChange} />
      </div>
      <button
        type="button"
        onClick={onSave}
        disabled={saving}
        data-testid="custom-fields-save"
        className="brand-btn mt-4 py-2.5 px-6 text-sm font-semibold disabled:opacity-60"
      >
        {saving ? "Saving…" : "Save custom fields"}
      </button>
    </div>
  );
}
